import React, {useEffect, useRef, useState} from "react";
import WaveSurfer from "wavesurfer.js";

import {makeStyles} from "@material-ui/core/styles";
import {Box, Button, Dialog, DialogContent, IconButton, Popover, Typography} from "@material-ui/core";

import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";

import TimelinePlugin from "wavesurfer.js/dist/plugins/timeline";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions";
import {Close} from "@material-ui/icons";
import {inject, observer} from "mobx-react";
import {ReactComponent as TooltipTipIcon} from "../common/images/TooltipTipIcon.svg";
import {ReactComponent as TooltipClose} from "../common/images/TooltipClose.svg";
import clsx from "clsx";


const useStyles = makeStyles(theme => ({
    dialogBox:{
        '& .MuiDialog-paper':{
            margin:0,
            background: 'white',
            boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.25)',
            // padding: '12px 10px 0px',
            padding: '0px',
            borderRadius: '5px 5px 5px 5px',
            boxSizing: 'border-box',
            minWidth: 1200
        },
        '& *':{
            fontFamily: 'NanumSquare',
        }
    },
    dialogTopBar: {
        alignItems:'center',
        display:'flex',
        justifyContent:'space-between',
    },
    titleText:{
        fontSize: '1rem',
        lineHeight: '22px',
        color: 'black',
        fontWeight: 600,
        padding: '10px 0 10px 17px',

    },
    iconButton:{
        padding: '9px 14px',
        '&:hover':{
            background: 'transparent'
        }
    },
    contentsBox:{
        background: '#fff',
        padding: 17,
        boxSizing: 'border-box',
    },
    controlBox:{
        marginTop: 25,
        position: 'relative',
        '& .MuiIconButton-root': {
            background: '#333',
            color: '#fff',
        }
    },
    disabled: {
        display: 'flex',
        justifyContent: 'right',
        position: 'absolute',
        right: 0,
        bottom: 0,
        '&.MuiBox-root': {
            padding: 0,
        },
        '& .MuiButtonBase-root': {
            background: 'rgba(16, 18, 28, 0.3)',
            color: '#fff',
            minWidth: 104,
            height: 34,
            '&:hover': {
                background: 'rgba(16, 18, 28, 0.3)',
            },
            '& .MuiTypography-root': {
                fontSize: '0.875rem',
                fontWeight: 'bold',
                color: 'rgba(255, 255, 255, 0.7)',
            }
        },
    },
    buttonStyle: {
        display: 'flex',
        justifyContent: 'right',
        '& .MuiButtonBase-root': {
            background: '#f27c57',
            color: '#fff',
            minWidth: 104,
            height: 34,
            '&:hover': {
                background: '#f27c57',
            },
            '& .MuiTypography-root': {
                fontSize: '0.875rem',
                fontWeight: 'bold',
                color: '#fff'
            }
        }
    },
    popoverTooltip: {
        '& .MuiPopover-paper': {
            background: '#365edf',
            color: '#fff',
            minWidth: 270,
            minHeight: 90,
            padding: '10px 10px 10px 15px',
            overflow: 'visible',
            marginTop: 15,
        },
        '& .MuiTypography-root': {
            color: '#fff',
            fontSize: '15px',
            lineHeight: '21px',
            letterSpacing: '-0.5px',
        }
    },
    popoverTooltipBottom: {
        '& .MuiPopover-paper': {
            background: '#365edf',
            color: '#fff',
            minWidth: 400,
            minHeight: 66,
            padding: '10px 10px 10px 15px',
            overflow: 'visible',
            marginTop: 0,
        },
        '& .MuiTypography-root': {
            color: '#fff',
            fontSize: '15px',
            lineHeight: '21px',
            letterSpacing: '-0.5px',
        }
    },
    tooltipTop: {
        position: 'absolute',
        top: -10,
        left: '50%',
        color: '#000'
    },
    tooltipBottom: {
        position: 'absolute',
        bottom: -15,
        left: '50%',
        color: '#000',
        '& svg': {
            transform: 'rotate(180deg)',
        }
    },
    tooltipBtn: {
        '& .MuiButtonBase-root': {
            padding: 0,
            minWidth: 'auto',
            '&:hover': {
                background: 'transparent'
            }
        }
    },
}));

function AudioPlayer(props) {
    const classes = useStyles();

    const [isPlaying, setIsPlaying] = useState(false);
    const {audioPlayerStore, audio, regionCount, close, callback} = props;

    const waveform = useRef(null);
    const [refVisible, setRefVisible] = useState(false);
    const wavesurfer = useRef(null);

    const divide = useRef(null);

    const timeline = TimelinePlugin.create({
        container: '#timelineform',
        height: 20,
        insertPosition: 'beforebegin',
        timeInterval: 0.5,
        primaryLabelInterval: 5,
        secondaryLabelInterval: 1,
        style: {
            fontSize: '10px',
            color: '#black',
            position: 'relative'
        },
        formatTimeCallback: function(seconds) {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = Math.floor(seconds % 60);
            return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
        }
    });

    useEffect(() => {
        if (!refVisible) {
            return;
        }
        if (waveform.current) {
            wavesurfer.current = WaveSurfer.create({
                container: waveform.current,
                waveColor: "black",
                // progressColor: "blue",
                height: 120,
                cursorWidth: 1,
                cursorColor: "lightgray",
                barWidth: 2,
                normalize: true,
                responsive: true,
                fillParent: true,
                plugins: [RegionsPlugin.create(), timeline],
            });

            wavesurfer.current.on("play", () => setIsPlaying(true));
            wavesurfer.current.on("pause", () => setIsPlaying(false));
            wavesurfer.current.on("click", () => generateRegion(wavesurfer.current.getDuration()));

            wavesurfer.current.plugins[0].on("region-clicked", (region, e) => {
                if (audioPlayerStore.selectedAudioRegion) {
                    if (audioPlayerStore.selectedAudioRegion.id === region.id) {
                        audioPlayerStore.setSelectedAudioRegion(null);
                        wavesurfer.current.seekTo(0);
                    } else {
                        audioPlayerStore.setSelectedAudioRegion(region);
                    }
                } else {
                    audioPlayerStore.setSelectedAudioRegion(region);
                }
            });

            wavesurfer.current.plugins[0].on('region-created', (region) => {
                const childList = region.element.children;

                for (const child of childList) {
                    const resize = child.getAttribute('data-resize');
                    if (resize === 'left') {
                        child.style.display = 'none';
                    }
                    if (resize === 'right') {
                        child.style.display = 'inline-block';
                        child.style.border = '6px solid transparent';
                        child.style.borderTopColor = 'black';
                        child.style.right = '-7px';
                        child.style.position = 'absolute';
                        child.style.width = '1px';

                        if (audioPlayerStore.audioRegionList.length === 0) {
                            divide.current = child;
                            handleOpenDividePopover();
                        }
                    }
                }
            })

            wavesurfer.current.plugins[0].on('region-updated', (updateRegion) => {
                const timeDiff = updateRegion.end - updateRegion.start;
                const isRemove = timeDiff < 0.01;

                const removeRegionList = wavesurfer.current.plugins[0].getRegions().filter(region => region.id !== updateRegion.id && region.start >= updateRegion.start && region.end <= updateRegion.end);
                for (const removeRegion of removeRegionList) {
                    audioPlayerStore.removeAudioRegion(removeRegion);
                    removeRegion.remove();
                }

                const afterRegion = wavesurfer.current.plugins[0].getRegions().filter(region => region.id !== updateRegion.id && updateRegion.end >= region.start && updateRegion.end <= region.end)[0];
                if (afterRegion) {
                    afterRegion.setOptions({start: updateRegion.end})
                } else {
                    const afterRegion = wavesurfer.current.plugins[0].getRegions().reduce((closest, current) => {
                        if (current.start > updateRegion.end) {
                            const currentDiff = Math.abs(current.start - updateRegion.end);
                            const closestDiff = Math.abs(closest.start - updateRegion.end);

                            return currentDiff < closestDiff ? current : closest;
                        }
                        return closest;
                    }, { start: Infinity, end: Infinity });
                    if (afterRegion.start !== Infinity && afterRegion.end !== Infinity) {
                        afterRegion.setOptions({start: isRemove ? updateRegion.start : updateRegion.end})
                    }
                }

                if (isRemove) {
                    audioPlayerStore.removeAudioRegion(updateRegion);
                    updateRegion.remove()
                }
            })

            wavesurfer.current.plugins[0].on("region-in", (region, e) => {
                if (audioPlayerStore.selectedAudioRegion) {
                    if (region.end !== audioPlayerStore.selectedAudioRegion.start) {
                        if (audioPlayerStore.selectedAudioRegion.id !== region.id) {
                            wavesurfer.current.pause();
                        }
                    }
                }
            })

            const decodeAudio = atob(audio);
            const blob = new Blob([new Uint8Array(decodeAudio.length).map((_, i) => decodeAudio.charCodeAt(i))], { type: 'audio/mp3' });
            wavesurfer.current.load(URL.createObjectURL(blob));
            audioPlayerStore.setAudioRegionList([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refVisible, audio]);

    const random = (min, max) => Math.random() * (max - min) + min
    const randomColor = () => `rgba(${random(0, 255)}, ${random(0, 255)}, ${random(0, 255)}, 0.2)`

    const [anchorEl, setAnchorEl] = useState(0);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const handleOpenPopover = () => {
        setAnchorEl(waveform.current);
        audioPlayerStore.setPopoverState(true);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
        audioPlayerStore.setPopoverState(null);
    };


    const [divideAnchorEl, setDivideAnchorEl] = useState(0);
    const divideOpen = Boolean(divideAnchorEl);
    const divideId = divideOpen ? 'simple-divide-popover' : undefined;

    const handleOpenDividePopover = () => {
        setDivideAnchorEl(divide.current);
        audioPlayerStore.setDividePopoverState(true);
    };

    const handleCloseDividePopover = () => {
        setDivideAnchorEl(null);
        audioPlayerStore.setDividePopoverState(null);
    };

    const togglePlayback = () => {
        if (!isPlaying) {
            if (audioPlayerStore.selectedAudioRegion) {
                audioPlayerStore.selectedAudioRegion.play();
            } else {
                wavesurfer.current.play();
            }
        } else {
            wavesurfer.current.pause();
        }
    };

    const generateRegion = (duration) => {
        if (audioPlayerStore.isProcessing) {
            wavesurfer.current.pause();
            if (audioPlayerStore.audioRegionList.length < regionCount) {
                const regionList = wavesurfer.current.plugins[0].getRegions();
                const regionCount = regionList.length;

                const currentTime = wavesurfer.current.getCurrentTime();
                if (regionCount !== 0) {
                    const divideRegion = regionList.filter(region => currentTime >= region.start && currentTime <= region.end)[0];

                    const newRegion = wavesurfer.current.plugins[0].addRegion({
                        start: currentTime,
                        end: divideRegion.end,
                        color: randomColor(),
                        resize: true,
                        drag: false,
                    });

                    audioPlayerStore.addAudioRegion(newRegion);
                    audioPlayerStore.setSelectedAudioRegion(newRegion);
                    divideRegion.setOptions({end: currentTime});
                } else {
                    const firstRegion = wavesurfer.current.plugins[0].addRegion({
                        start: 0,
                        end: currentTime,
                        color: randomColor(),
                        resize: true,
                        drag: false,
                    });
                    audioPlayerStore.addAudioRegion(firstRegion);

                    const lastRegion = wavesurfer.current.plugins[0].addRegion({
                        start: currentTime,
                        end: duration,
                        color: randomColor(),
                        resize: true,
                        drag: false,
                    });
                    audioPlayerStore.addAudioRegion(lastRegion);
                    audioPlayerStore.setSelectedAudioRegion(lastRegion);
                }

                audioPlayerStore.sortAudioRegionList();
            }
        }
    }

    const handleClickClose = () => {
        audioPlayerStore.closeModal();
        close && close();
    }

    const handleClickFinish = () => {
        audioPlayerStore.closeModal();
        callback && callback();
        close && close();
    }

    const handleClickDivide = () => {
        audioPlayerStore.setIsProcessing(true);
        handleOpenPopover();
    }

    let transportPlayButton;

    if (!isPlaying) {
        transportPlayButton = (
            <IconButton onClick={togglePlayback}>
                <PlayArrowIcon className={classes.icon} />
            </IconButton>
        );
    } else {
        transportPlayButton = (
            <IconButton onClick={togglePlayback}>
                <PauseIcon className={classes.icon} />
            </IconButton>
        );
    }

    return (
        <Dialog
            open={audioPlayerStore.isOpen}
            className={classes.dialogBox}
        >
            <Box
                className={classes.dialogTopBar}
            >
                <Typography className={classes.titleText}>사운드 생성</Typography>
                <IconButton className={classes.iconButton} onClick={handleClickClose} disableRipple>
                    <Close/>
                </IconButton>
            </Box>
            <DialogContent dividers>
                <Box className={classes.contentsBox}>
                    <div id='waveform' ref={el => { waveform.current = el; setRefVisible(!!el); }} className={classes.waveform} >
                        <div id='timelineform' />
                    </div>
                    {/*상단 툴팁*/}
                    <Popover
                        id={divideId}
                        open={audioPlayerStore.dividePopoverState === null ? false : audioPlayerStore.dividePopoverState}
                        anchorEl={divideAnchorEl}
                        onClose={handleCloseDividePopover}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        style={{
                            marginTop: '-10px',
                            marginLeft: '-10px'
                        }}
                        className={classes.popoverTooltipBottom}
                    >
                        <Box className={classes.tooltipBottom}>
                            <TooltipTipIcon />
                        </Box>
                        <Box display={'flex'} justifyContent={'flex-end'} className={classes.tooltipBtn}>
                            <Button disableRipple onClick={handleCloseDividePopover}>
                                <TooltipClose />
                            </Button>
                        </Box>
                        <Typography className={classes.typography}>마커 위치를 조정하여 나누어진 단락 영역을 수정할 수 있습니다.</Typography>
                    </Popover>
                    {/*하단 툻팁*/}
                    <Popover
                        id={id}
                        open={audioPlayerStore.popoverState === null ? false : audioPlayerStore.popoverState}
                        anchorEl={anchorEl}
                        onClose={handleClosePopover}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        className={classes.popoverTooltip}
                    >
                        <Box className={classes.tooltipTop}>
                            <TooltipTipIcon />
                        </Box>
                        <Box display={'flex'} justifyContent={'flex-end'} className={classes.tooltipBtn}>
                            <Button disableRipple onClick={handleClosePopover}>
                                <TooltipClose />
                            </Button>
                        </Box>
                        <Typography className={classes.typography}>음성을 나누고 싶은 위치를 클릭 하세요.</Typography>
                        <Typography className={classes.typography}>해당 위치 앞뒤로 영역이 나누어집니다.</Typography>
                    </Popover>
                    <Box display='flex' justifyContent='center' alignItems='center' className={classes.controlBox}>
                        {transportPlayButton}
                        {audioPlayerStore.isProcessing ?
                            <Box className={clsx(classes.disabled, (audioPlayerStore.audioRegionList.length === regionCount ? classes.buttonStyle : null))} sx={{marginLeft: 10}}>
                                <Button disabled={audioPlayerStore.audioRegionList.length < regionCount} disableRipple onClick={handleClickFinish}>
                                    <Typography>완료</Typography>
                                </Button>
                            </Box>
                            :
                            <Box className={clsx(classes.disabled, classes.buttonStyle)} sx={{marginLeft: 10}}>
                                <Button onClick={handleClickDivide}>
                                    <Typography>단락 나누기</Typography>
                                </Button>
                            </Box>
                        }
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default inject('audioPlayerStore')(observer(AudioPlayer));
